import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useClubPermissions } from '../hooks/useClubPermissions';
import { SeasonProvider } from '../context/SeasonContext';
import { deleteSession } from '../services/sessionManagementService';
import Button from '../components/common/Button';
import ConfirmationModal from '../components/common/ConfirmationModal';
import { BackButton } from '../components/common';
import { Heading, Text } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import { navigateBack } from '../utils/navigationUtils';
import { useClubSessionsHub } from '../hooks/useClubSessionsHub';
import SessionsTodayWork from '../components/sessions/hub/SessionsTodayWork';
import SessionsPastSection from '../components/sessions/hub/SessionsPastSection';
import SessionsArchivedSection from '../components/sessions/hub/SessionsArchivedSection';
import SessionsPageShell from '../components/sessions/hub/SessionsPageShell';
import { getTodayWorkInboxSessionIds } from '../components/sessions/hub/sessionHubUtils';
import ClubLogo from '../components/clubs/ClubLogo';

const SessionReviewPage = ({ currentUser }) => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [pastSectionExpanded, setPastSectionExpanded] = useState(true);
  const [archivedSectionExpanded, setArchivedSectionExpanded] = useState(false);
  const [todayWorkExpanded, setTodayWorkExpanded] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [mutationError, setMutationError] = useState(null);

  const {
    club: selectedClub,
    role: clubRole,
    loading: clubLoading,
    hasPermission,
    error: clubError,
  } = useClubPermissions(clubId, currentUser);

  const {
    todayWork,
    todayWorkOverflowWrapUpCount,
    completedSessionAllMarked,
    loading,
    error: hubError,
    refresh: refreshHubSessions,
  } = useClubSessionsHub(clubId, { scope: 'review' });

  const isAdmin =
    currentUser?.role === 'admin' ||
    currentUser?.role === 'super_admin' ||
    clubRole === 'host' ||
    clubRole === 'admin' ||
    clubRole === 'owner';

  const error = mutationError || hubError;

  const todayWorkInboxSessionIds = useMemo(
    () => getTodayWorkInboxSessionIds(todayWork),
    [todayWork]
  );

  const handleViewOverflowArchive = useCallback(() => {
    setPastSectionExpanded(true);
  }, []);

  const handleDeleteSession = (sessionOrId) => {
    if (typeof sessionOrId === 'object' && sessionOrId?.id) {
      setSessionToDelete(sessionOrId);
    } else {
      const id = sessionOrId;
      const fromTodayWork = todayWork.find((item) => item.session?.id === id)?.session;
      setSessionToDelete(fromTodayWork || { id, name: 'this session' });
    }
    setShowDeleteConfirm(true);
  };

  const confirmDeleteSession = async () => {
    if (!sessionToDelete?.id) return;
    try {
      await deleteSession(sessionToDelete.id);
      setShowDeleteConfirm(false);
      setSessionToDelete(null);
      setMutationError(null);
      await refreshHubSessions();
    } catch (err) {
      console.error('Error deleting session:', err);
      setMutationError(err.message || 'Failed to delete session');
    }
  };

  if (!currentUser) {
    return (
      <SessionsPageShell>
        <Card variant="default" size="medium" className="text-center">
          <Heading level={2} className="mb-4">Please Log In</Heading>
          <Text className="mb-6">You need to be logged in to review sessions.</Text>
          <Button onClick={() => navigate('/')} variant="primary">
            Go to Login
          </Button>
        </Card>
      </SessionsPageShell>
    );
  }

  if (clubLoading) {
    return (
      <SessionsPageShell>
        <Card variant="default" size="medium" className="py-12 text-center">
          <Text>Loading club details...</Text>
        </Card>
      </SessionsPageShell>
    );
  }

  if (clubError || !hasPermission || !isAdmin) {
    return (
      <SessionsPageShell>
        <Card variant="default" size="medium" className="py-12 text-center">
          <Heading level={2} className="mb-4">Access Denied</Heading>
          <Text className="mb-6 text-text-secondary">
            {clubError || 'You do not have permission to review sessions for this club'}
          </Text>
          <Button onClick={() => navigate('/')} variant="primary">
            Go to Homepage
          </Button>
        </Card>
      </SessionsPageShell>
    );
  }

  return (
    <SeasonProvider clubId={clubId}>
      <SessionsPageShell>
        <div className="mb-6 sm:mb-8">
          <BackButton
            hideOnDesktop
            className="mb-2"
            onClick={() => {
              navigateBack(navigate, location, clubId, `/club-management/${clubId}`);
            }}
          >
            Back to Management
          </BackButton>
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-3">
              {selectedClub ? (
                <ClubLogo
                  logoImageUrl={selectedClub.logo_image_url}
                  clubName={selectedClub.name}
                  size="sm"
                  className="border border-border-medium shadow-brand-sm"
                />
              ) : null}
              <Heading level={1} className="min-w-0 truncate text-xl font-bold text-text-primary sm:text-2xl">
                Review sessions
              </Heading>
            </div>
            <Text className="mt-1 text-sm text-text-secondary">
              Wrap up payments and archive past sessions for {selectedClub?.name}
            </Text>
          </div>
        </div>

        {error ? (
          <Card variant="danger" size="medium" className="mb-6">
            <Text className="text-sm">{error}</Text>
          </Card>
        ) : null}

        <div className="space-y-6 sm:space-y-8">
          {loading ? (
            <Text className="text-text-secondary">Loading to-do list...</Text>
          ) : (
            <SessionsTodayWork
              items={todayWork}
              includeTypes={['wrap-up', 'delete']}
              overflowWrapUpCount={todayWorkOverflowWrapUpCount}
              clubId={clubId}
              clubLogoUrl={selectedClub.logo_image_url}
              clubName={selectedClub.name}
              starterPlayerId={currentUser?.id}
              onDeleteSession={handleDeleteSession}
              onViewOverflowArchive={handleViewOverflowArchive}
              expanded={todayWorkExpanded}
              onExpandedChange={setTodayWorkExpanded}
              sectionTitle="To do list"
            />
          )}

          <SessionsPastSection
            clubId={clubId}
            defaultExpanded={true}
            expanded={pastSectionExpanded}
            onExpandedChange={setPastSectionExpanded}
            excludeSessionIds={todayWorkInboxSessionIds}
            completedSessionAllMarked={loading ? null : completedSessionAllMarked}
            onSessionsChange={refreshHubSessions}
            clubLogoUrl={selectedClub.logo_image_url}
            clubName={selectedClub.name}
          />

          <SessionsArchivedSection
            clubId={clubId}
            defaultExpanded={false}
            expanded={archivedSectionExpanded}
            onExpandedChange={setArchivedSectionExpanded}
            completedSessionAllMarked={loading ? null : completedSessionAllMarked}
            onSessionsChange={refreshHubSessions}
            clubLogoUrl={selectedClub.logo_image_url}
            clubName={selectedClub.name}
          />
        </div>

        <ConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setSessionToDelete(null);
          }}
          onConfirm={confirmDeleteSession}
          title="Delete Session"
          message={
            sessionToDelete?.name
              ? `Are you sure you want to permanently delete "${sessionToDelete.name}"? This will remove the session and all player signups from the system. This action cannot be undone.`
              : 'Are you sure you want to permanently delete this session? This will remove the session and all player signups from the system. This action cannot be undone.'
          }
          confirmText="Delete Session"
          cancelText="Keep Session"
          isDangerous={true}
        />
      </SessionsPageShell>
    </SeasonProvider>
  );
};

export default SessionReviewPage;
